/**
 * Approximate EmailBison's HTML→plain-text rendering for previews.
 *
 * When a campaign is in `plain_text=true` mode, Bison converts the HTML
 * body to a `text/plain` MIME part on send. Empirical testing (FIR-7120)
 * confirmed Bison's converter is markdown-style:
 *
 *   <p>...</p><p>...</p>           →  paragraph blank line (\n\n)
 *   <br>                            →  single newline
 *   <strong>bold</strong>           →  plain text (no formatting)
 *   <em>italic</em>                 →  plain text (no formatting)
 *   <a href="X">text</a>            →  [text](X)
 *   <ul><li>x</li><li>y</li></ul>   →  - x\n- y
 *
 * This util mirrors that behavior on the frontend so previews match what
 * recipients actually see. It uses DOMParser (browser-only) — do not call
 * this from server-side code.
 */
export function htmlToBisonPlainText(html: string): string {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')

  function walk(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
    if (node.nodeType !== Node.ELEMENT_NODE) return ''
    const el = node as Element
    const tag = el.tagName.toLowerCase()

    if (tag === 'br') return '\n'

    if (tag === 'a') {
      const href = el.getAttribute('href') ?? ''
      const text = childText(el)
      return href && text !== href ? `[${text}](${href})` : text || href
    }

    if (tag === 'li') return `- ${childText(el)}\n`

    const inner = childText(el)
    // Block-level tags get paragraph spacing around them.
    if (
      tag === 'p' ||
      tag === 'div' ||
      tag === 'h1' ||
      tag === 'h2' ||
      tag === 'h3' ||
      tag === 'h4' ||
      tag === 'h5' ||
      tag === 'h6' ||
      tag === 'blockquote' ||
      tag === 'ul' ||
      tag === 'ol'
    ) {
      return inner ? `${inner}\n\n` : '\n'
    }
    return inner
  }

  function childText(el: Element): string {
    let out = ''
    el.childNodes.forEach((c) => {
      out += walk(c)
    })
    return out
  }

  return walk(doc.body)
    .replace(/\n{3,}/g, '\n\n') // collapse runs of more than 2 newlines
    .replace(/[ \t]+\n/g, '\n') // strip trailing spaces on lines
    .trim()
}
